import cn from 'classnames'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import React, { ChangeEvent, ReactElement, ReactNode } from 'react'
import { useTranslate } from 'src/localization/Trns'

import style from './EditAccessAcknowledgement.style'

export interface EditAccessAcknowledgementProps {
  className?: string
  checked: boolean
  onChange: (checked: boolean) => void
  /**
   * Intro sentence(s) above the bullet list. Defaults to the wording shown on
   * the registered-account summary — consumers override only when the
   * surrounding context needs different framing.
   */
  intro?: ReactNode
  testId?: string
}

/**
 * The edit-access acknowledgement shown when granting a user the ability to
 * create / close / update virtual cards and budgets. Mirrors the block on the
 * registered-account summary so the legal copy stays in one place; the
 * checkbox is controlled by the consumer so it can gate a submit action.
 */
const EditAccessAcknowledgement: React.FC<EditAccessAcknowledgementProps> = ({
  className,
  checked,
  onChange,
  intro,
  testId = 'edit-access-acknowledgement',
}): ReactElement => {
  const translate = useTranslate()

  const action = (label: string): ReactElement => (
    <span css={style.action}>{translate(label)}</span>
  )

  return (
    <div css={style.container} className={cn(className)} data-testid={testId}>
      <div css={style.titleRow}>
        <Checkbox
          aria-label={translate('I acknowledge edit access')}
          checked={checked}
          onChange={(e: ChangeEvent<HTMLInputElement>): void =>
            onChange(e.currentTarget.checked)
          }
          data-testid={`${testId}-checkbox`}
        />
        <p css={style.title}>{translate('I acknowledge that...')}</p>
      </div>
      <p css={style.body}>
        {intro ??
          translate(
            'This user will have edit access over the above listed accounts. This means that they will be able to create, update and close virtual cards and budgets under the below listed accounts.',
          )}
      </p>
      <ul css={style.list}>
        <li>
          {translate('They can ${action} virtual cards and budgets', {
            action: action('create'),
          })}
        </li>
        <li>
          {translate('They can ${action} virtual cards and budgets', {
            action: action('close'),
          })}
        </li>
        <li>
          {translate('They can ${action} virtual card and budget details', {
            action: action('update'),
          })}
        </li>
      </ul>
      <p css={style.body}>
        {translate(
          "All actions will be logged in the 'History' of each virtual card or budget, including details of the user who performed the action.",
        )}
      </p>
    </div>
  )
}

export default EditAccessAcknowledgement
